// Футърът на „Целия списък": лентата с избраните („Избрани N", при доставка и
// „· сума X €"; чип за всеки — с въведеното количество, — последно отметнатият
// пръв, × го маха; „Изчисти") и действията — „+ Нов
// продукт" (ако листът го поддържа), „Отказ" и основното „Добави N избрани"
// с Kbd chip Ctrl+Enter. На телефон: лентата горе, основният бутон на цяла
// ширина, под него второстепенните (рецептата на модалите в cloudts-design).
import { useTranslation } from "react-i18next";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { KbdChip } from "../KbdChip";
import {
  LINK_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../list/buttonStyles";
import { formatMoney } from "../numbers";
import type { CatalogItem } from "./catalogPickerModel";
import type { CatalogEntryValues } from "./catalogEntryModel";

type Props = {
  pickedItems: CatalogItem[];
  // въведеното в редовете (с `entry`) — количеството в чипа
  entries?: CatalogEntryValues;
  // сумата нето на отметнатите (само доставка), иначе null
  pickedSum?: number | null;
  isNarrow: boolean;
  query: string;
  onUnpick: (id: number) => void;
  onClear: () => void;
  onAdd: () => void;
  onCancel: () => void;
  onCreate?: () => void;
};

// „Всички видими" може да отметне хиляди редове — чиповете са само за
// последните, останалите са „+ още N"
const TRAY_CHIP_LIMIT = 50;

// Десктоп: кликът по лентата не взема фокуса от филтъра
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

export const CatalogSelectionTray = ({
  pickedItems,
  entries,
  pickedSum = null,
  isNarrow,
  query,
  onUnpick,
  onClear,
  onAdd,
  onCancel,
  onCreate,
}: Props) => {
  const { t } = useTranslation();
  const count = pickedItems.length;
  const mouseDown = isNarrow ? undefined : keepFocus;
  const trimmedQuery = query.trim();

  const tray = (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {count ? (
        <span className="shrink-0 whitespace-nowrap text-[13px] font-semibold text-ink">
          {t("inventoryPage.catSelected", { count })}
          {pickedSum != null && (
            <span className="font-normal text-body">
              {" · "}
              {t("inventoryPage.catSelectedSum")}{" "}
              <span className="font-sheet font-semibold tabular-nums text-ink">{formatMoney(pickedSum)} €</span>
            </span>
          )}
        </span>
      ) : (
        <span className="truncate text-[12.5px] text-muted">{t("inventoryPage.catNothingSelected")}</span>
      )}
      {count > 0 && (
        <div
          className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:thin]"
          // колелото на мишката скролира лентата настрани
          onWheel={(event) => {
            if (event.deltaY) event.currentTarget.scrollLeft += event.deltaY;
          }}
        >
          {pickedItems.slice(0, TRAY_CHIP_LIMIT).map((item) => {
            const qty = entries?.get(item.id)?.qty.trim();
            return (
            <span
              key={item.id}
              className="inline-flex max-w-[240px] shrink-0 items-center gap-1 rounded-full border border-brand/35 bg-brand-tint py-[3px] pl-2.5 pr-1 text-xs leading-[normal] text-brand-deep-ink"
            >
              <span className="truncate">{item.name}</span>
              {qty && (
                <span className="shrink-0 whitespace-nowrap font-sheet font-semibold tabular-nums">
                  {qty}
                  {item.entryUnit ? <span className="font-sans font-normal"> {item.entryUnit}</span> : null}
                </span>
              )}
              <button
                type="button"
                aria-label={t("inventoryPage.catUnpick", { name: item.name })}
                title={t("inventoryPage.catUnpick", { name: item.name })}
                onMouseDown={mouseDown}
                onClick={() => onUnpick(item.id)}
                className="flex h-[18px] w-[18px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-[10px] text-brand-deep-ink outline-none hover:bg-brand-tint2 focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint2)]"
              >
                <CloseOutlined aria-hidden />
              </button>
            </span>
            );
          })}
          {count > TRAY_CHIP_LIMIT && (
            <span className="shrink-0 self-center whitespace-nowrap text-xs text-muted">
              {t("inventoryPage.catMore", { count: count - TRAY_CHIP_LIMIT })}
            </span>
          )}
        </div>
      )}
      {count > 0 && (
        <button type="button" onMouseDown={mouseDown} onClick={onClear} className={`${LINK_BUTTON_CLASS} shrink-0`}>
          {t("inventoryPage.filterClear")}
        </button>
      )}
    </div>
  );

  const createButton = onCreate ? (
    <button
      type="button"
      onClick={onCreate}
      className={`${SECONDARY_BUTTON_CLASS} ${isNarrow ? "min-w-0 flex-1" : "max-w-[240px]"}`}
    >
      <PlusOutlined />
      <span className="truncate">
        {trimmedQuery ? t("inventoryPage.catCreateNamed", { name: trimmedQuery }) : t("inventoryPage.productAdd")}
      </span>
    </button>
  ) : null;
  const cancelButton = (
    <button type="button" onClick={onCancel} className={`${SECONDARY_BUTTON_CLASS} ${isNarrow ? "flex-1" : ""}`}>
      {t("inventoryPage.cancel")}
    </button>
  );
  const addButton = (
    <button
      type="button"
      disabled={!count}
      onClick={onAdd}
      aria-keyshortcuts="Control+Enter"
      className={`${PRIMARY_BUTTON_CLASS} ${isNarrow ? "w-full !py-3" : ""}`}
    >
      {count ? t("inventoryPage.catAddCount", { count }) : t("inventoryPage.catalogAdd")}
      {/* клавишът е в aria-keyshortcuts — chip-ът не влиза в името на бутона */}
      {!isNarrow && (
        <span aria-hidden>
          <KbdChip>Ctrl+Enter</KbdChip>
        </span>
      )}
    </button>
  );

  if (isNarrow)
    return (
      <footer className="flex flex-col gap-2.5 border-t border-shade-2 bg-canvas px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)] pt-3">
        {tray}
        {addButton}
        <div className="flex gap-2">
          {createButton}
          {cancelButton}
        </div>
      </footer>
    );
  return (
    <footer className="flex items-center gap-3 border-t border-shade-2 bg-canvas px-[22px] py-3">
      {tray}
      <div className="flex shrink-0 items-center gap-2">
        {createButton}
        {cancelButton}
        {addButton}
      </div>
    </footer>
  );
};

export default CatalogSelectionTray;
